import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ChannelTypeEnum } from "../../apiClient/generated";
import { useNotificationsApi } from "../../hooks";
import { PrimaryButton } from "../../ui/Buttons";
import { ConfirmationDialog } from "../../ui/ConfirmationDialog";
import { Modal } from "../../ui/Modals";

interface ExternalEmailsModalProps {
    company?: number;
    disabled?: boolean;
    visible: boolean;
    onClose: () => void;
}

export const ExternalEmailsModal = (props: ExternalEmailsModalProps) => {
    const apiClient = useNotificationsApi();
    const queryClient = useQueryClient();
    const [channelToDelete, setChannelToDelete] = useState<{
        id: number;
        email?: string | null;
    }>();

    const channelsQuery = useQuery({
        queryKey: ["notificationChannelsList", props.company],
        queryFn: async () =>
            await apiClient.notificationsChannelsList({
                owner: props.company,
                pageSize: 100,
            }),
        enabled: props.visible,
    });

    const groupsQuery = useQuery({
        queryKey: ["notificationGroup", props.company],
        queryFn: async () =>
            await apiClient.notificationsGroupsList({
                owner: props.company,
                pageSize: 100,
            }),
        enabled: props.visible,
    });

    const invalidateChannels = () => {
        queryClient.invalidateQueries({
            queryKey: ["notificationChannelsList"],
        });
        queryClient.invalidateQueries({
            queryKey: ["notificationGroup"],
        });
        queryClient.invalidateQueries({
            queryKey: ["emissionNotificationSettings"],
        });
    };

    const deleteChannelMutation = useMutation({
        mutationFn: async (id: number) =>
            await apiClient.notificationsChannelsDestroy({ id }),
        onSuccess: () => {
            setChannelToDelete(undefined);
            invalidateChannels();
        },
    });

    const resendConfirmationMutation = useMutation({
        mutationFn: async (id: number) =>
            await apiClient.notificationsChannelsResendEmailConfirmationCreate({
                id,
            }),
    });

    const channels = (channelsQuery.data?.results ?? []).filter(
        (channel) => channel.channelType === ChannelTypeEnum.Email,
    );
    const groups = groupsQuery.data?.results ?? [];
    const closeModal = () => {
        setChannelToDelete(undefined);
        props.onClose();
    };

    return (
        <>
            <Modal
                size="md"
                visible={props.visible}
                onClose={() => {
                    if (!channelToDelete) {
                        closeModal();
                    }
                }}
            >
                <div className="mt-2">
                    <h2 className="text-xl font-semibold text-slate-900">
                        External emails
                    </h2>
                    <p className="mt-1 max-w-2xl text-sm text-slate-500">
                        Addresses outside the platform. You add them from within
                        a group, and each one confirms by email before alerts
                        are sent.
                    </p>
                </div>

                <div className="-mx-6 mt-6 border-y border-slate-100">
                    {channelsQuery.isLoading || groupsQuery.isLoading ? (
                        <p className="px-8 py-6 text-sm text-slate-500">
                            Loading external emails…
                        </p>
                    ) : channelsQuery.isError || groupsQuery.isError ? (
                        <p className="px-8 py-6 text-sm text-red-700">
                            Unable to load external emails.
                        </p>
                    ) : channels.length === 0 ? (
                        <p className="px-8 py-6 text-sm text-slate-500">
                            No external emails have been added yet.
                        </p>
                    ) : (
                        channels.map((channel) => {
                            const groupCount = groups.filter((group) =>
                                group.channels.some(
                                    (groupChannel) =>
                                        groupChannel.id === channel.id,
                                ),
                            ).length;
                            const isPending = !channel.active;

                            return (
                                <div
                                    key={channel.id}
                                    className="flex items-center gap-4 border-b border-slate-100 px-8 py-4 last:border-b-0"
                                >
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-dashed border-amber-800 bg-amber-50 text-sm font-semibold text-amber-800">
                                        @
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-mono text-sm font-semibold text-slate-900">
                                            {channel.email}
                                        </p>
                                        <p className="text-sm text-slate-500">
                                            In {groupCount}{" "}
                                            {groupCount === 1
                                                ? "group"
                                                : "groups"}
                                            {isPending &&
                                                " · awaiting email confirmation"}
                                        </p>
                                    </div>
                                    <span
                                        className={`rounded px-2.5 py-1 text-sm font-semibold ${
                                            isPending
                                                ? "bg-amber-50 text-amber-800"
                                                : "bg-emerald-50 text-emerald-800"
                                        }`}
                                    >
                                        {isPending ? "Pending" : "Confirmed"}
                                    </span>
                                    {isPending && !props.disabled && (
                                        <button
                                            type="button"
                                            disabled={
                                                resendConfirmationMutation.isLoading
                                            }
                                            onClick={() =>
                                                resendConfirmationMutation.mutate(
                                                    channel.id,
                                                )
                                            }
                                            className="rounded px-2 py-1 text-sm font-semibold text-ae-blue-900 transition-colors hover:bg-blue-50 disabled:cursor-wait"
                                        >
                                            {resendConfirmationMutation.isLoading
                                                ? "Sending…"
                                                : "Resend"}
                                        </button>
                                    )}
                                    {!props.disabled && (
                                        <button
                                            type="button"
                                            aria-label={`Delete ${channel.email}`}
                                            disabled={
                                                deleteChannelMutation.isLoading
                                            }
                                            onClick={() =>
                                                setChannelToDelete(channel)
                                            }
                                            className="rounded p-2 text-slate-500 transition-colors hover:bg-red-50 disabled:cursor-wait"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="h-5 w-5"
                                            />
                                        </button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="mt-4 flex justify-end">
                    <PrimaryButton type="button" onClick={closeModal}>
                        Done
                    </PrimaryButton>
                </div>
            </Modal>
            <ConfirmationDialog
                visible={!!channelToDelete}
                onClose={() => setChannelToDelete(undefined)}
                onConfirm={() =>
                    channelToDelete &&
                    deleteChannelMutation.mutate(channelToDelete.id)
                }
                loading={deleteChannelMutation.isLoading}
                title="Delete external email?"
                message={
                    <p>
                        Are you sure you want to delete the email{" "}
                        {channelToDelete?.email}?
                    </p>
                }
                confirmText="Delete"
            />
        </>
    );
};
